import { useGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { usePostOffersCargosApprove } from "@/entities/post-offers-cargos-approve/fetch";
import { usePostOffersCargosReject } from "@/entities/post-offers-cargos-reject/fetch";
import { usePostOffersCargos } from "@/entities/post-offers-cargos/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { fetchOfferCargoProcess } from "@/features/offer-cargo-process/fetch";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { safeParseInt } from "@/shared/lib/number";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { AdminContractDetailPageProps, DraftOfferCargo } from "../model";

const concatAddress = (draftOfferCargo: DraftOfferCargo) => {
  const destinations = draftOfferCargo.destinations.map((destination) => ({
    ...destination,
    city: destination.city,
    addressLine: destination.addressLine,
    address: destination.addressLine
      ? `${destination.city} ${destination.addressLine}`
      : destination.city,
  }));
  return { ...draftOfferCargo, destinations };
};

export const useAdminContractDetailPageProps = (
  contractIdStr?: string,
): AdminContractDetailPageProps => {
  const contractId = useMemo(() => {
    if (!contractIdStr) {
      return undefined;
    }
    const id = safeParseInt(contractIdStr);
    if (!id) {
      return undefined;
    }
    return id;
  }, [contractIdStr]);

  if (!contractId) {
    throw new Error("contractId is required");
  }

  // State
  const [previewValues, setPreviewValues] =
    useState<AdminContractDetailPageProps["previewValues"]>(null);

  // Local Storage
  // 記入途中の情報をローカルストレージに保存する
  const [inProgressValues, setInProgressValues, removeInProgressValues] =
    useInProgressValuesLocalCache<
      AdminContractDetailPageProps["defaultInputValues"]
    >("AdminContractDetailPage");

  // Backend API
  const getOffersCargo = useGetOffersCargo(
    {
      id: contractId,
    },
    {
      enabled: !!contractId,
    },
  );

  const postOffersCargos = usePostOffersCargos();
  const postOffersCargosApprove = usePostOffersCargosApprove();
  const postOffersCargosReject = usePostOffersCargosReject();

  // Props
  const { offerCargoProcess, refetch: refetchOfferCargoProcess } =
    fetchOfferCargoProcess(getOffersCargo.data?.data.process.id);

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Actions
  const navigate = useNavigate();

  const onInputValuesUpdate = useCallback<
    AdminContractDetailPageProps["onInputValuesUpdate"]
  >(
    (draftOfferCargo) => {
      setInProgressValues(draftOfferCargo);
    },
    [setInProgressValues],
  );

  const onConfirmClick = useCallback<
    AdminContractDetailPageProps["onConfirmClick"]
  >(async (draftOfferCargo) => {
    setPreviewValues(draftOfferCargo);
  }, []);

  const onBackToInProgressClick = useCallback<
    AdminContractDetailPageProps["onBackToInProgressClick"]
  >(() => {
    setPreviewValues(null);
  }, []);

  const onSubmitClick = useCallback<
    AdminContractDetailPageProps["onSubmitClick"]
  >(
    async (draftOfferCargo) => {
      const response = await postOffersCargos.mutateAsync({
        data: { ...concatAddress(draftOfferCargo), vehicleId: null },
      });
      if (response && isSuccessStatusCode(response.status)) {
        setGlobalNotification({
          variant: "success",
          message: "荷物の新規登録が完了しました",
          autoHide: true,
        });
        removeInProgressValues();
        navigate({
          to: "/admin/cargos",
        });
      }
    },
    [
      postOffersCargos.mutateAsync,
      removeInProgressValues,
      setGlobalNotification,
      navigate,
    ],
  );

  const onApproveClick = useCallback<
    AdminContractDetailPageProps["onApproveClick"]
  >(() => {
    void (async () => {
      if (!contractId) {
        console.error("Contract ID is required for approval");
        return;
      }
      const response = await postOffersCargosApprove.mutateAsync({
        id: contractId.toString(),
      });
      if (response && isSuccessStatusCode(response.status)) {
        setGlobalNotification({
          variant: "success",
          message: "契約に合意しました",
          autoHide: true,
        });
        await refetchOfferCargoProcess();
      } else {
        setGlobalNotification({
          variant: "error",
          message: "契約の合意に失敗しました",
          autoHide: true,
        });
      }
    })();
  }, [
    contractId,
    postOffersCargosApprove,
    refetchOfferCargoProcess,
    setGlobalNotification,
  ]);

  const onRejectClick = useCallback<
    AdminContractDetailPageProps["onRejectClick"]
  >(
    async (params) => {
      if (!contractId) {
        console.error("Contract ID is required for rejection");
        return;
      }
      const response = await postOffersCargosReject.mutateAsync({
        id: contractId.toString(),
        data: params,
      });
      if (response && isSuccessStatusCode(response.status)) {
        setGlobalNotification({
          variant: "success",
          message: "契約を見送りました",
          autoHide: true,
        });
        removeInProgressValues();
        // 見送り後はページに留まる
      }
    },
    [
      contractId,
      postOffersCargosReject.mutateAsync,
      removeInProgressValues,
      setGlobalNotification,
    ],
  );

  return {
    contractId,
    offerCargoProcess,
    defaultInputValues: inProgressValues,
    previewValues,
    onInputValuesUpdate,
    onBackToInProgressClick,
    onSubmitClick,
    onConfirmClick,
    onApproveClick,
    onRejectClick,
  };
};
